import { Button, ButtonProps, Image, Text } from 'components';
import styled from '@emotion/styled';
import NoResultsImg from 'img/no-results.png';
import NoDataImg from 'img/no-data.png';
import { Link } from 'react-router-dom';
import { t } from '@lingui/macro';
import { useTheme, CircularProgress } from '@mui/material';

interface NoResultsProps {
  type: 'noResults' | 'noData';
  title?: string;
  description?: string;
  img?: string;
  alt?: string;
  showAction?: boolean;
  actionProps?: ButtonProps;
  addRow?: (() => void) | string;
  loading?: boolean;
}

const NoResults = ({
  type,
  title,
  description,
  img,
  alt,
  actionProps,
  showAction,
  addRow,
  loading = false,
}: NoResultsProps) => {
  const theme = useTheme();
  const content = {
    noResults: {
      img: NoResultsImg,
      alt: t`No results found`,
      title: t`No results found`,
      description: t`We couldn’t find any entries matching your search. Try adjusting your search term or clear filters to see all results.`,
    },
    noData: {
      img: NoDataImg,
      alt: t`No data found`,
      title: t`No data exists`,
      description: t`Create your first item.`,
    },
  };
  if (loading) {
    return (
      <Container>
        <Loading>{loading ? <CircularProgress /> : null}</Loading>
      </Container>
    );
  }
  return (
    <Container>
      <Image
        src={img || content[type].img}
        alt={alt || content[type].alt}
        width={224}
        height={224}
      />
      <TitleDescriptionContainer>
        <Text variant="headlineXs" weight="semiBold">
          {title || content[type].title}
        </Text>
        <Text
          variant="bodySm"
          weight="regular"
          style={{ textAlign: 'center' }}
          color={theme.palette.text.tertiary}
        >
          {description || content[type].description}
        </Text>
      </TitleDescriptionContainer>
      {(showAction || actionProps?.children) && typeof addRow === 'string' ? (
        <Link to={addRow}>
          <Button {...actionProps} sx={{ marginTop: '24px', ...actionProps?.sx }} />
        </Link>
      ) : null}
      {(showAction || actionProps?.children) && addRow && typeof addRow !== 'string' ? (
        <Button
          {...actionProps}
          onClick={() => addRow()}
          sx={{ marginTop: '24px', ...actionProps?.sx }}
        />
      ) : null}
    </Container>
  );
};

const Loading = styled.div`
  height: 40px;
  margin-top: 24px;
`;

const Container = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const TitleDescriptionContainer = styled.div`
  width: 348px;
  height: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

export default NoResults;
